import { validatedDisplayView } from "../../i18n/display-view.js";
/**
 * The two things a customer ASKS WeTalk for, because they cannot do them alone yet (fix round
 * 2026-09-25, package HI):
 *
 *   - a phone number (customer-portal review #49) — `POST/GET /v1/numbers/requests`;
 *   - more concurrent conversations (review #115, #59) — `POST/GET /v1/quota-requests`.
 *
 * Both record a request an operator reads from the staff API and acts on with the operator routes
 * that already exist. The methods are merged onto `ConsoleGateway` here, so the area that owns the
 * screens owns the shape; the sandbox's half is `../pending-request-sandbox.ts` (a slice never
 * imports a sandbox module).
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import {
  MalformedResponseError,
  readArray,
  readRecord,
  readText,
  readWholeNumber,
} from "../decode.js";
import { readNullableText } from "../conversation-view.js";
import type { SliceContext } from "./slice-context.js";

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** This workspace's number requests, newest first (review #49). */
    listNumberRequests(): Promise<readonly NumberRequestView[]>;
    /** Asks WeTalk to set up a number for one agent. Owner and Admin only. */
    requestPhoneNumber(draft: NumberRequestDraftView): Promise<NumberRequestView>;
    /** This workspace's requests for more concurrent conversations (review #115, #59). */
    listQuotaRequests(): Promise<readonly QuotaRequestView[]>;
    /** Asks WeTalk for more concurrent conversations. Owner and Admin only. */
    requestConcurrencyQuota(draft: QuotaRequestDraftView): Promise<QuotaRequestView>;
  }
}

export interface NumberRequestDraftView {
  readonly voiceAgentId: string;
  /** ISO-3166 alpha-2. */
  readonly country: string;
  readonly note: string;
}

export interface NumberRequestView {
  readonly numberRequestId: string;
  readonly voiceAgentId: string;
  readonly voiceAgentName: string;
  readonly country: string;
  readonly note: string;
  /** "Today 14:02". */
  readonly requestedAtText: string;
  /** Derived by the API: `connected` once a number answers for that agent. */
  readonly state: "pending" | "connected";
  readonly connectedE164: string | null;
}

export interface QuotaRequestDraftView {
  readonly requestedChannelUnit: number;
  readonly justification: string;
}

export interface QuotaRequestView {
  readonly quotaRequestId: string;
  readonly requestedChannelUnit: number;
  readonly justification: string;
  /** `pending` until an operator decides it. */
  readonly state: string;
  readonly requestedAtText: string;
}

export function numberRequestOfWire(
  value: unknown,
  path: string,
  now: Date,
  formatWhen: SliceContext["formatWhen"],
): NumberRequestView {
  const one = readRecord(value, path);
  const state = readText(one["state"], `${path}.state`);
  if (state !== "pending" && state !== "connected") {
    throw new MalformedResponseError(`${path}.state`, "pending or connected", state);
  }
  return validatedDisplayView({
    numberRequestId: readText(one["number_request_id"], `${path}.number_request_id`),
    voiceAgentId: readText(one["voice_agent_id"], `${path}.voice_agent_id`),
    voiceAgentName: readText(one["voice_agent_name"], `${path}.voice_agent_name`),
    country: readText(one["country"], `${path}.country`),
    get note() { return readText(one["note"], `${path}.note`); },
    get requestedAtText() { return formatWhen(readText(one["requested_at"], `${path}.requested_at`), now); },
    state,
    connectedE164: readNullableText(one["connected_e164"] ?? null, `${path}.connected_e164`),
  });
}

export function quotaRequestOfWire(
  value: unknown,
  path: string,
  now: Date,
  formatWhen: SliceContext["formatWhen"],
): QuotaRequestView {
  const one = readRecord(value, path);
  return validatedDisplayView({
    quotaRequestId: readText(one["quota_request_id"], `${path}.quota_request_id`),
    requestedChannelUnit: readWholeNumber(one["requested_channel_unit"], `${path}.requested_channel_unit`),
    justification: readText(one["justification"], `${path}.justification`),
    state: readText(one["state"], `${path}.state`),
    get requestedAtText() { return formatWhen(readText(one["requested_at"], `${path}.requested_at`), now); },
  });
}

export function pendingRequestApiSlice(
  client: WeTalkApiClient,
  context: SliceContext,
): Pick<
  ConsoleGateway,
  "listNumberRequests" | "requestPhoneNumber" | "listQuotaRequests" | "requestConcurrencyQuota"
> {
  return {
    async listNumberRequests(): Promise<readonly NumberRequestView[]> {
      const now = context.now();
      const body = readRecord(assertOk(await client.listNumberRequests()), "listNumberRequests");
      return readArray(body["data"], "listNumberRequests.data").map((one, index) =>
        numberRequestOfWire(one, `numberRequest[${String(index)}]`, now, context.formatWhen),
      );
    },

    async requestPhoneNumber(draft: NumberRequestDraftView): Promise<NumberRequestView> {
      const body = readRecord(
        assertOk(
          await client.requestPhoneNumber({
            body: { voice_agent_id: draft.voiceAgentId, country: draft.country, note: draft.note },
          }),
        ),
        "requestPhoneNumber",
      );
      return numberRequestOfWire(body["data"], "numberRequest", context.now(), context.formatWhen);
    },

    async listQuotaRequests(): Promise<readonly QuotaRequestView[]> {
      const now = context.now();
      const body = readRecord(assertOk(await client.listQuotaRequests()), "listQuotaRequests");
      return readArray(body["data"], "listQuotaRequests.data").map((one, index) =>
        quotaRequestOfWire(one, `quotaRequest[${String(index)}]`, now, context.formatWhen),
      );
    },

    async requestConcurrencyQuota(draft: QuotaRequestDraftView): Promise<QuotaRequestView> {
      const body = readRecord(
        assertOk(
          await client.requestConcurrencyQuota({
            body: {
              requested_channel_unit: draft.requestedChannelUnit,
              justification: draft.justification,
            },
          }),
        ),
        "requestConcurrencyQuota",
      );
      return quotaRequestOfWire(body["data"], "quotaRequest", context.now(), context.formatWhen);
    },
  };
}
